import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  PageBody,
  TableActionsMenu,
} from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { NewTopicSetDialog } from '@/components/topics/NewTopicSetDialog.tsx';
import { createTopicSet, deleteTopicSet, listTopicSets } from '@/lib/topicSets.ts';
import type { TopicSet } from '@/types/topicSets.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { ApiError } from '@wundo/client/api.ts';

export const Route = createFileRoute('/_app/topics/')({
  staticData: { orgScope: 'required' },
  component: TopicsPage,
});

/**
 * Who may act on a set. The global default (no organisation) is ours alone, so
 * an organisation's admin only reads it. The backend policy has the same rule;
 * this keeps the console from offering what the API would refuse.
 */
function canManage(set: TopicSet, role: string | undefined): boolean {
  return role === 'superadmin' || set.organisation_id !== null;
}

const columns: DataTableColumn<TopicSet>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (set) => (
      <span className='inline-flex items-center gap-2'>
        {set.name}
        {set.organisation_id === null && <Badge tone='info'>Default</Badge>}
      </span>
    ),
  },
  {
    key: 'topics',
    header: 'Topics',
    width: 'w-24',
    render: (set) => set.topics.length,
  },
];

/**
 * The topic sets an organisation may build tracks on: its own, with the global
 * default at the head of the list. Reached from a track's Topics tab rather than
 * the rail: it is where you fix the themes a track offers.
 */
export function TopicsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  /* The organisation this console is being read through. */
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id;

  const {
    data: sets,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['topicSets', organisationId],
    queryFn: () => listTopicSets(organisationId!),
    enabled: organisationId !== undefined,
  });

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deleting, setDeleting] = useState<TopicSet | null>(null);

  /* A new set is a name and nothing else, so it opens on its own page to be filled in. */
  const create = useMutation({
    mutationFn: (name: string) =>
      createTopicSet({ organisation_id: organisationId!, name }),
    onSuccess: async (set) => {
      setCreating(false);
      await queryClient.invalidateQueries({ queryKey: ['topicSets'] });
      await router.navigate({ to: '/topics/$id', params: { id: set.id } });
    },
    onError: (failure) =>
      setCreateError(apiErrorMessage(failure, 'Failed to create topic set')),
  });

  /*
   * DataTable types `rowHref` as always returning a URL, but reads `undefined`
   * as "this row does not navigate" — which is what the global default is for
   * anyone but a superadmin.
   */
  const rowHref = ((set: TopicSet) =>
    canManage(set, user?.role) ? `/topics/${set.id}` : undefined) as (
    set: TopicSet,
  ) => string;

  return (
    <>
      <PageHeader title='Topic sets' />
      <PageBody>
        <DataTable
          label='Topic sets'
          columns={columns}
          rows={sets ?? []}
          rowKey={(set) => set.id}
          rowHref={rowHref}
          onNavigate={(href) => void router.navigate({ href })}
          loading={isPending && organisationId !== undefined}
          error={isError ? 'Failed to load topic sets' : undefined}
          emptyState={
            <EmptyState
              title='No topic sets'
              description='Create one to give a track its own set of themes.'
            />
          }
          toolbar={
            <>
              <Button size='sm' variant='quiet' onClick={() => router.history.back()}>
                Back
              </Button>
              <Button
                size='sm'
                onClick={() => {
                  setCreateError('');
                  setCreating(true);
                }}
              >
                Create
              </Button>
            </>
          }
          actions={(set) => (
            <TableActionsMenu
              label={`Actions for ${set.name}`}
              items={
                canManage(set, user?.role)
                  ? [
                      {
                        label: 'Edit',
                        icon: <Pencil aria-hidden='true' />,
                        onSelect: () =>
                          void router.navigate({
                            to: '/topics/$id',
                            params: { id: set.id },
                          }),
                      },
                      {
                        label: 'Delete',
                        icon: <Trash2 aria-hidden='true' />,
                        tone: 'danger',
                        onSelect: () => setDeleting(set),
                      },
                    ]
                  : []
              }
            />
          )}
        />
      </PageBody>

      <NewTopicSetDialog
        open={creating}
        onOpenChange={(open) => {
          setCreating(open);
          if (!open) setCreateError('');
        }}
        saving={create.isPending}
        error={createError}
        onCreate={(name) => {
          setCreateError('');
          create.mutate(name);
        }}
      />

      <DeleteTopicSetDialog set={deleting} onClose={() => setDeleting(null)} />
    </>
  );
}

interface DeleteTopicSetDialogProps {
  /** The set to delete; null keeps the dialog closed. */
  set: TopicSet | null;
  onClose: () => void;
}

/**
 * Deleting a topic set. The endpoint refuses only while a track still reads the
 * set, so a 422 has one meaning and is worth saying out loud. The dialog stays
 * open on it: closing would take the reason with it.
 */
function DeleteTopicSetDialog({ set, onClose }: DeleteTopicSetDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const remove = useMutation({
    mutationFn: (doomed: TopicSet) => deleteTopicSet(doomed.id),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['topicSets'] });
    },
    onError: (failure) =>
      setError(
        apiErrorMessage(
          failure,
          failure instanceof ApiError && failure.status === 422
            ? 'This topic set is in use by a track and cannot be deleted'
            : 'Failed to delete topic set',
        ),
      ),
  });

  return (
    <ConfirmDialog
      open={set !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setError('');
        }
      }}
      title='Delete topic set?'
      description={`Delete "${set?.name ?? ''}"? This cannot be undone.`}
      confirmLabel='Delete'
      cancelLabel='Cancel'
      variant='danger'
      error={error}
      onConfirm={async () => {
        if (!set) return;
        setError('');
        await remove.mutateAsync(set).catch(() => undefined);
      }}
    />
  );
}
